import useIntersectionObserver from "@base/model/hooks/useIntersectionObserver";
import { ResponsiveSheet } from "@base/ui/ResponsiveOverlay";
import { useSafeAreaInset } from "@base/ui/SafeArea/useSafeAreaInset";
import { Button } from "@buildblockinc/buildblock-design-system";
import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { History, MessageCircleMore, Send, SquarePen, X } from "lucide-react";
import { Fragment, Suspense, memo, useEffect, useMemo } from "react";
import type { FileHistoryEvent, FileHistoryEventType } from "../model/schema";
import { useInfiniteFileHistoryQuery } from "../model/useFileHistoryQuery";

dayjs.extend(relativeTime);

const EVENT_TYPE_LABELS: Record<FileHistoryEventType, string> = {
  "DAILY_LOG.CREATED": "Created a report",
  "DAILY_LOG.MODIFIED": "Modified a report",
  "DAILY_LOG.DELETED": "Deleted a report",
  "NOTIFICATION.SENT": "Sent a notification",
  "COMMENT.CREATED": "Left a comment",
  "COMMENT.MODIFIED": "Edited a comment",
  "COMMENT.DELETED": "Deleted a comment",
};

interface FileHistoryBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  logId: string;
}

export function FileHistoryBottomSheet({ isOpen, onClose, projectId, logId }: FileHistoryBottomSheetProps) {
  return (
    <ResponsiveSheet isOpen={isOpen} onClose={onClose} maxHeight={100} ariaLabel="File History">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center px-4 pb-6 bg-white shrink-0 md:py-4">
          <h2 className="text-title6 text-gray-1000">File History</h2>
          <button type="button" onClick={onClose} aria-label="Close file history">
            <X width={24} height={24} className="text-gray-1000" />
          </button>
        </div>

        {isOpen ? (
          <ErrorBoundary
            fallback={({ resetError }) => (
              <FileHistoryListError projectId={projectId} logId={logId} onRetry={resetError} />
            )}
          >
            <Suspense fallback={<FileHistoryListSkeleton />}>
              <FileHistoryList projectId={projectId} logId={logId} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <div className="flex-1" />
        )}
      </div>
    </ResponsiveSheet>
  );
}

interface FileHistoryListProps {
  projectId: string;
  logId: string;
}

function FileHistoryList({ projectId, logId }: FileHistoryListProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteFileHistoryQuery(projectId, logId);

  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver();

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const events = data.pages.flatMap((page) => page.results);

  if (events.length === 0) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-1 text-center pb-4">
        <History width={30} height={30} className="text-gray-400" />
        <p className="text-body3 text-gray-600">No history yet.</p>
      </div>
    );
  }

  const { bottom } = useSafeAreaInset();

  return (
    <div className="flex flex-col flex-1 overflow-y-auto scrollbar-hide" style={{ paddingBottom: bottom }}>
      <ul className="flex flex-col px-4 pb-4">
        {events.map((event, index) => (
          <Fragment key={event.id}>
            <FileHistoryItem event={event} />
            {index < events.length - 1 && <div className="my-4 border-b border-gray-200" />}
          </Fragment>
        ))}
      </ul>

      <div ref={sentinelRef as (instance: HTMLDivElement | null) => void} className="h-1 shrink-0" />

      {isFetchingNextPage && (
        <div className="flex justify-center py-3 shrink-0">
          <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
        </div>
      )}
    </div>
  );
}

function FileHistoryListSkeleton() {
  return (
    <ul className="flex flex-col flex-1 px-4 pb-4 overflow-y-auto animate-pulse">
      {Array.from({ length: 6 }, (_, i) => `history-skeleton-${i}`).map((key, index, arr) => (
        <Fragment key={key}>
          <li className="flex gap-x-2 items-center py-1">
            <div className="w-10 h-10 rounded-full skeleton-shimmer shrink-0" />
            <div className="flex flex-col gap-y-1.5 flex-1">
              <div className="h-3.5 w-24 rounded-4 skeleton-shimmer" />
              <div className="h-3 w-32 rounded-4 skeleton-shimmer" />
            </div>
            <div className="w-12 h-3 rounded-4 skeleton-shimmer" />
          </li>
          {index < arr.length - 1 && <div className="my-4 border-b border-gray-200" />}
        </Fragment>
      ))}
    </ul>
  );
}

interface FileHistoryListErrorProps {
  projectId: string;
  logId: string;
  onRetry: () => void;
}

function FileHistoryListError({ projectId, logId, onRetry }: FileHistoryListErrorProps) {
  const queryClient = useQueryClient();

  const handleRetry = () => {
    queryClient.resetQueries({ queryKey: ["file-history", projectId, logId] });
    onRetry();
  };

  return (
    <div className="flex flex-col flex-1 items-center justify-center gap-3 px-4 pb-4 text-center">
      <p className="text-body2 text-gray-700">Failed to load file history.</p>
      <Button onClick={handleRetry} variant="outline" color="info">
        Try Again
      </Button>
    </div>
  );
}

const FileHistoryItem = memo(function FileHistoryItem({ event }: { event: FileHistoryEvent }) {
  const historyIcon = useMemo(() => {
    switch (event.type) {
      case "NOTIFICATION.SENT":
        return (
          <div className="w-10 h-10 rounded-full bg-green-10 shrink-0 flex items-center justify-center">
            <Send width={20} height={20} className="text-green-700" />
          </div>
        );
      case "DAILY_LOG.MODIFIED":
        return (
          <div className="w-10 h-10 rounded-full bg-blue-10 shrink-0 flex items-center justify-center">
            <SquarePen width={20} height={20} className="text-blue-500" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-full bg-gray-10 shrink-0 flex items-center justify-center">
            <MessageCircleMore width={20} height={20} className="text-gray-700" />
          </div>
        );
    }
  }, [event.type]);

  return (
    <li className="flex gap-x-2 items-start justify-between">
      <div className="flex gap-x-2 items-center">
        {historyIcon}
        <div className="flex-1 min-w-0">
          <p className="truncate text-subtitle6 text-gray-1000">{event.user.name}</p>
          <p className="text-gray-800 text-body3">{EVENT_TYPE_LABELS[event.type]}</p>
        </div>
      </div>
      <span className="text-gray-500 shrink-0 text-caption1">{dayjs(event.created_at).fromNow()}</span>
    </li>
  );
});
